import { PostAuthLayout, PreAuthLayout } from '@/components/layouts';
import ErrorBoundary from '@/components/particles/ErrorBoundary';
import MinimumScreenSizeBoundary from '@/components/particles/MinimumScreenSizeBoundary';
import { FormProvider } from '@/components/providers';
import { ToastProvider } from '@/components/providers/common/ToastProvider';
import PostAuthProvidersWrapper from '@/components/providers/postauth/PostAuthProvidersWrapper';
import { AuthProvider } from '@/components/providers/preauth/AuthProvider';
import APP_CONFIG from '@/config/app';
import useInitFlowbite from '@/lib/hooks/useInitFlowbite';
import reactQueryClient from '@/services/ReactQueryClientService';
import '@/styles/globals.css';
import { preAuthRoutes } from '@/utils/constants';
import { aktiv, archivo, inter, roboto, sfPro, workSans } from '@/utils/fonts';
import { logError, logInfo } from '@/utils/logger';
import clarity from '@microsoft/clarity';
import { QueryClientProvider } from '@tanstack/react-query';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
const AppWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (
    process.env.NODE_ENV === 'development' &&
    APP_CONFIG.DISABLE_STRICT_MODE_IN_DEVELOPMENT
  ) {
    return <>{children}</>;
  }
  return <React.StrictMode>{children}</React.StrictMode>;
};
interface CustomAppProps extends Omit<AppProps, 'Component'> {
  Component: AppProps['Component'] & {
    getLayout?(page: React.ReactNode): JSX.Element;
  };
}
export default function App({ Component, pageProps }: CustomAppProps) {
  const router = useRouter();
  useInitFlowbite();
  useEffect(() => {
    const clarityProjectId = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID;
    logInfo(`Clarity Project ID: ${clarityProjectId}`, 'App.useEffect');
    if (clarityProjectId) {
      try {
        clarity.init(clarityProjectId);
        logInfo('Clarity init call completed', 'App.useEffect');
      } catch (e) {
        logError(e, 'App.useEffect', 'Clarity init call failed');
      }
    }
  }, []);
  const isPreAuthPage = preAuthRoutes.includes(router.pathname);
  const Layout = isPreAuthPage ? PreAuthLayout : PostAuthLayout;
  return (
    <ErrorBoundary>
      <AppWrapper>
        <div>
          <style jsx global>{`
            :root {
              --font-archivo: ${archivo.style.fontFamily};
              --font-inter: ${inter.style.fontFamily};
              --font-roboto: ${roboto.style.fontFamily};
              --font-sfpro: ${sfPro.style.fontFamily};
              --font-aktiv: ${aktiv.style.fontFamily};
              --font-work-sans: ${workSans.style.fontFamily};
            }
          `}</style>
          <MinimumScreenSizeBoundary>
            <div>
              <FormProvider>
                <ToastProvider>
                  <AuthProvider>
                    <QueryClientProvider client={reactQueryClient}>
                      <Head>
                        <title>Sliver</title>
                      </Head>
                      <div
                        className="fixed inset-0 w-full h-full bg-accent pointer-events-none z-[-1]"
                        style={{
                          background:
                            'linear-gradient(rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 33%)',
                        }}
                      />
                      {isPreAuthPage ? (
                        <div className="flex flex-col p-lg">
                          <Layout>
                            {Component.getLayout ? (
                              Component.getLayout(<Component {...pageProps} />)
                            ) : (
                              <Component {...pageProps} />
                            )}
                          </Layout>
                        </div>
                      ) : (
                        <PostAuthProvidersWrapper>
                          <div className="flex flex-col p-lg">
                            <Layout>
                              {Component.getLayout ? (
                                Component.getLayout(
                                  <Component {...pageProps} />,
                                )
                              ) : (
                                <Component {...pageProps} />
                              )}
                            </Layout>
                          </div>
                        </PostAuthProvidersWrapper>
                      )}
                    </QueryClientProvider>
                  </AuthProvider>
                </ToastProvider>
              </FormProvider>
            </div>
          </MinimumScreenSizeBoundary>
        </div>
      </AppWrapper>
    </ErrorBoundary>
  );
}
